import { useState, useEffect } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import { Search, Calendar, X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react'

function GalleryPage() {
  const [photos, setPhotos] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedEvent, setSelectedEvent] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  useEffect(() => {
    loadGallery()
  }, [])

  const loadGallery = async () => {
    setLoading(true)
    try {
      // Charger toutes les photos
      const photosQuery = query(
        collection(db, 'gallery_photos'),
        orderBy('uploadedAt', 'desc')
      )
      const photosSnapshot = await getDocs(photosQuery)
      const photosData = photosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setPhotos(photosData)

      // Extraire les événements uniques
      const uniqueEvents = [...new Set(photosData.map(p => p.eventName))].filter(Boolean)
      setEvents(uniqueEvents)
    } catch (error) {
      console.error('Erreur chargement galerie:', error)
      setPhotos([])
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    } catch (e) {
      return ''
    }
  }

  const filteredPhotos = photos.filter(photo => {
    const matchEvent = selectedEvent === 'all' || photo.eventName === selectedEvent
    const matchSearch = searchTerm === '' || 
      photo.eventName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      photo.description?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchEvent && matchSearch
  })

  // Grouper par événement
  const photosByEvent = filteredPhotos.reduce((acc, photo) => {
    const eventName = photo.eventName || 'Sans événement'
    if (!acc[eventName]) {
      acc[eventName] = []
    }
    acc[eventName].push(photo)
    return acc
  }, {})

  const openLightbox = (photoUrl, index, eventPhotos) => {
    setLightboxImage({ url: photoUrl, photos: eventPhotos })
    setLightboxIndex(index)
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxIndex(0)
  }

  const nextImage = () => {
    if (lightboxImage && lightboxIndex < lightboxImage.photos.length - 1) {
      setLightboxIndex(lightboxIndex + 1)
    }
  }

  const prevImage = () => {
    if (lightboxImage && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1)
    }
  }

  return (
    <div className="page active">
      <div className="container">
        <div className="page-header">
          <div className="page-badge">📸 GALERIE</div>
          <h1 className="page-title">Galerie Photos</h1>
          <p className="page-subtitle">Souvenirs de nos événements Johnny</p>
        </div>

        {/* Filtres */}
        <div style={{ marginBottom: '30px' }}>
          <div className="glass-card" style={{ marginBottom: '15px', padding: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Search size={20} color="rgba(255,255,255,0.5)" />
              <input
                type="text"
                placeholder="Rechercher dans la galerie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  outline: 'none',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* Filtres par événement */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSelectedEvent('all')}
              className="btn"
              style={{
                padding: '10px 20px',
                background: selectedEvent === 'all' 
                  ? 'linear-gradient(135deg, #00d9ff, #0ea5e9)' 
                  : 'rgba(255,255,255,0.05)',
                border: selectedEvent === 'all' ? 'none' : '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                fontWeight: 600,
                color: '#fff'
              }}
            >
              Tous ({photos.length})
            </button>
            {events.map((eventName) => (
              <button
                key={eventName}
                onClick={() => setSelectedEvent(eventName)}
                className="btn"
                style={{
                  padding: '10px 20px',
                  background: selectedEvent === eventName 
                    ? 'linear-gradient(135deg, #ffd700, #f59e0b)' 
                    : 'rgba(255,255,255,0.05)',
                  border: selectedEvent === eventName ? 'none' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  fontWeight: 600,
                  color: '#fff'
                }}
              >
                {eventName} ({photos.filter(p => p.eventName === eventName).length})
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Chargement de la galerie...</p>
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📸</div>
            <p>{searchTerm ? 'Aucune photo trouvée' : 'Aucune photo dans la galerie'}</p>
          </div>
        ) : (
          <div>
            {Object.entries(photosByEvent).map(([eventName, eventPhotos]) => (
              <div key={eventName} style={{ marginBottom: '50px' }}>
                <h2 style={{
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  <Calendar size={24} color="#00d9ff" />
                  {eventName}
                  <span style={{ 
                    fontSize: '1rem', 
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 400 
                  }}>
                    ({eventPhotos.length} photo{eventPhotos.length > 1 ? 's' : ''})
                  </span>
                </h2>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {eventPhotos.map((photo, index) => (
                    <div
                      key={photo.id}
                      onClick={() => openLightbox(photo.photoURL, index, eventPhotos)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '15px',
                        overflow: 'hidden',
                        position: 'relative',
                        aspectRatio: '1',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)'
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 217, 255, 0.3)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <img
                        src={photo.photoURL}
                        alt={photo.description || eventName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {photo.description && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: '15px',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                          color: '#fff',
                          fontSize: '0.9rem'
                        }}>
                          {photo.description}
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        padding: '6px 10px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        📷
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="dock-spacer"></div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={closeLightbox}
        >
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10001
            }}
            onClick={(e) => {
              e.stopPropagation()
              closeLightbox()
            }}
          >
            <X size={24} color="#fff" />
          </button>

          {lightboxIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                prevImage()
              }}
              style={{
                position: 'absolute',
                left: '20px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10001
              }}
            >
              <ChevronLeft size={24} color="#fff" />
            </button>
          )}

          {lightboxIndex < lightboxImage.photos.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                nextImage()
              }}
              style={{
                position: 'absolute',
                right: '20px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10001
              }}
            >
              <ChevronRight size={24} color="#fff" />
            </button>
          )}

          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={lightboxImage.photos[lightboxIndex].photoURL}
              alt={lightboxImage.photos[lightboxIndex].description}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '10px'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            
            {lightboxImage.photos[lightboxIndex].description && (
              <div style={{
                position: 'absolute',
                bottom: '-60px',
                left: 0,
                right: 0,
                textAlign: 'center',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 600,
                textShadow: '0 2px 10px rgba(0,0,0,0.8)'
              }}>
                {lightboxImage.photos[lightboxIndex].description}
              </div>
            )}

            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              padding: '10px 20px',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              {lightboxIndex + 1} / {lightboxImage.photos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GalleryPage